import type { BendAngleDeg } from '../model/types.js';
import type { PinState, BendState } from '../model/pin.js';
import { makeInitialPinState } from '../model/pin.js';
import type { PinDef } from '../library/component-def.js';

/**
 * Operaciones puras sobre el estado de un pin.
 * Devuelven un PinState nuevo (inmutabilidad para facilitar undo/redo).
 * No conocen ni el DOM ni Konva.
 */

const BEND_STEP = 45;
/**
 * Rango permitido por eje y por punto de doblez.
 * Físicamente, un doblez de terminal más allá de 90° dobla el pin sobre
 * sí mismo; geométricamente hace que la trayectoria colapse o se doble
 * hacia atrás sin sentido en el free-form dead-bug. Se restringe la
 * acumulación a [-90°, +90°] en cada eje independientemente.
 */
const BEND_MIN = -135;
const BEND_MAX = 135;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Snap a múltiplo de 45° y clamp a [BEND_MIN, BEND_MAX]. */
function snapAndClamp(angle: BendAngleDeg): BendAngleDeg {
  const snapped = Math.round(angle / BEND_STEP) * BEND_STEP;
  return clamp(snapped, BEND_MIN, BEND_MAX);
}

export type BendDirection = 'left' | 'right' | 'up' | 'down';

/**
 * Aplica +45° en la dirección indicada al punto de doblez `bendIndex`.
 * Si el pin está cortado antes o en `bendIndex`, la operación se
 * considera no válida y se devuelve el estado sin cambios.
 */
export function bend(
  state: PinState,
  bendIndex: number,
  direction: BendDirection,
): PinState {
  if (bendIndex < 0 || bendIndex >= state.bends.length) return state;
  if (state.cut_at_index !== null && bendIndex >= state.cut_at_index) return state;

  const current = state.bends[bendIndex];
  if (current === undefined) return state;

  const next: BendState = { ...current };
  switch (direction) {
    case 'right':
      next.angle_in_plane_deg = snapAndClamp(current.angle_in_plane_deg + BEND_STEP);
      break;
    case 'left':
      next.angle_in_plane_deg = snapAndClamp(current.angle_in_plane_deg - BEND_STEP);
      break;
    case 'up':
      next.angle_out_of_plane_deg = snapAndClamp(
        current.angle_out_of_plane_deg + BEND_STEP,
      );
      break;
    case 'down':
      next.angle_out_of_plane_deg = snapAndClamp(
        current.angle_out_of_plane_deg - BEND_STEP,
      );
      break;
  }

  const newBends = state.bends.slice();
  newBends[bendIndex] = next;
  return { ...state, bends: newBends };
}

/**
 * Corta el pin en el punto de doblez indicado.
 * Los dobleces con índice ≥ cutIndex quedan inutilizados; se resetean a 0
 * para que si el usuario restaura y vuelve a partir del mismo estado, no
 * queden ángulos "fantasma".
 * Si `cutIndex` coincide con un doblez existente distinto de cero, ese
 * doblez desaparece (queda a 0), como pide el diseño.
 */
export function cut(state: PinState, cutIndex: number): PinState {
  if (cutIndex < 0 || cutIndex >= state.bends.length) return state;

  const newBends = state.bends.map((b, i) => {
    if (i >= cutIndex) {
      return { angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 };
    }
    return b;
  });
  return { ...state, bends: newBends, cut_at_index: cutIndex };
}

/**
 * Restaura el pin a su estado original: sin dobleces, sin cortes.
 * Necesita la definición para conocer el número de puntos de doblez.
 */
export function restore(def: PinDef, state: PinState): PinState {
  return makeInitialPinState(state.pin_id, def.bend_points_count);
}

/**
 * Devuelve true si un punto de doblez está más allá del corte y por tanto
 * debe deshabilitarse visualmente y rechazar operaciones de doblez.
 */
export function isBendPointCutAway(state: PinState, bendIndex: number): boolean {
  return state.cut_at_index !== null && bendIndex >= state.cut_at_index;
}
